import { useState } from "react";
import Head from "next/head";
import Image from "next/image";

import {
  Check,
  CircleCheckBig,
  FileText,
  MessageSquare,
  Mic,
  Monitor,
  Phone,
  Send,
  Smartphone,
  Star,
  Zap,
} from "lucide-react";

import useCalApi from "@/components/hooks/useCalApi";
import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import { UnderlineSVG } from "@/components/icons/HomePage";
import Marquee from "@/components/magicui/marquee";
import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import { BaseVideoModal } from "@/components/seo/VideoModalWithThumbnail";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { clashGrotesk, jakartaSans } from "@/styles/fonts";

const FAQ_ITEMS = [
  {
    question: "What's the difference between a quote and an estimate?",
    answer:
      "A quote is typically a fixed price you're committing to, while an estimate is a best-available price based on current info. Many home service companies use the terms interchangeably—Rebolt supports both, plus proposals and bids.",
  },
  {
    question: "Can I create a quote on my phone at a job site?",
    answer:
      "Yes. Rebolt is built for on-site quoting with iOS and Android apps, so you can build and send an estimate from the driveway or the truck.",
  },
  {
    question: "Can clients approve quotes online?",
    answer:
      "Yes. They can review and approve quotes through the client portal, helping you close jobs faster without phone tag.",
  },
  {
    question: "Can I add optional line items or upgrades?",
    answer:
      "Yes. You can add optional line items (add-ons, upgrades, alternates) so clients can choose what they want—great for increasing average ticket size.",
  },
  {
    question: "How do automated quote follow-ups work?",
    answer:
      'Rebolt automatically follows up on sent quotes that haven\'t been approved yet, helping you recover "forgotten" opportunities and keep your sales pipeline moving.',
  },
  {
    question: "What types of contractors use Rebolt quoting software?",
    answer:
      "Rebolt is designed for home service businesses—including plumbing, HVAC, electrical, roofing, landscaping, cleaning, and more.",
  },
];

export default function QuotingToolPage() {
  useCalApi();
  const { calPath } = useCalendarLink();

  return (
    <>
      <Head>
        <title>Home Service Quoting & Estimating Software | Rebolt</title>
        <meta
          name="description"
          content="Create professional quotes, estimates, bids, and proposals in minutes—on-site or at your desk. Voice to quote, price book, optional line items, automated follow-ups, and instant online approvals."
        />
        <meta property="og:title" content="Home Service Quoting & Estimating Software | Rebolt" />
        <meta
          property="og:description"
          content="Create professional quotes and estimates in minutes. Built for contractors, plumbers, HVAC techs, electricians, and home service pros."
        />
      </Head>

      <NavbarLegacy />

      <main className={cn("overflow-hidden pt-16 bg-clay-100 relative", jakartaSans.variable, clashGrotesk.variable)}>
        <div className="absolute inset-0 w-full h-screen pointer-events-none">
          <Image
            src="/HeroBG.png"
            alt="Background pattern"
            width={1000}
            height={500}
            className="object-cover w-full opacity-30"
            priority
          />
        </div>

        <div className="flex flex-col items-center justify-center relative">
          {/* Hero Section */}
          <HeroSection calPath={calPath} />

          {/* Trust Strip */}
          <TrustStrip />

          {/* Workflow Section */}
          <WorkflowSection />

          {/* Feature Block 1: Fast Quote Creation */}
          <FeatureBlockQuoteCreation />

          {/* Feature Block 2: Voice to Quote */}
          {/* <FeatureBlockVoiceToQuote calPath={calPath} /> */}

          {/* Feature Block 3: Price Book */}
          <FeatureBlockPriceBook />

          {/* Feature Block 4: Optional Line Items */}
          <FeatureBlockOptionalLineItems />

          {/* Feature Block 5: Online Approvals */}
          <FeatureBlockOnlineApprovals />

          {/* Feature Block 6: Automated Follow-Ups */}
          <FeatureBlockAutomatedFollowUps />

          {/* Platform Section */}
          <PlatformSection calPath={calPath} />

          {/* Internal Links */}
          {/* <InternalLinksSection /> */}

          {/* FAQ Section */}
          <FAQSection />

          {/* Footer */}
          <Footer
            footerCTA={
              <>
                <h2 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug text-left md:text-center relative text-white max-w-3xl">
                  Send a quote before you leave the{" "}
                  <span className="relative inline-block">
                    driveway
                    <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-full">
                      <UnderlineSVG className="w-full" />
                    </span>
                  </span>
                </h2>
                <div className="flex w-full justify-center mt-12">
                  <Button
                    size="xl"
                    variant="rebolt-primary"
                    data-cal-link={calPath}
                    data-cal-config='{"layout":"month_view"}'
                  >
                    Start a Free Trial
                  </Button>
                </div>
              </>
            }
          />
        </div>
      </main>
    </>
  );
}

// Placeholder demo video URL - replace with actual demo video when available
const DEMO_VIDEO_URL = "https://www.youtube.com/watch?v=dQw4w9WgXcQ";

function HeroSection({ calPath }: { calPath: string }) {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <>
      <section className="hero h-screen flex flex-col items-center justify-center gap-8 md:gap-10">
        <div className="flex flex-col gap-6 items-center justify-center px-4 max-w-[992px] text-ash-900">
          {/* Main Headline */}
          <h1 className="font-clash my-8 font-medium text-5xl md:text-7xl leading-[1.05] tracking-snug text-center relative">
            Home Service{" "}
            <span className="relative inline-block">
              Quoting
              <UnderlineSVG className="absolute -bottom-2.5 md:-bottom-4 left-1/2 -translate-x-1/2 w-full" />
            </span>{" "}
            & Estimating Software
          </h1>

          {/* Subhead */}
          <div className="flex flex-col gap-8">
            <p className="text-base md:text-xl text-center max-w-[760px]">
              Create quotes, estimates, bids, and proposals on-site or at your desk. Use voice to quote, a simple price
              book, optional line items, automated follow-ups, and instant online approvals.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
              <Button
                size="xl"
                variant="rebolt-primary"
                data-cal-link={calPath}
                data-cal-config='{"layout":"month_view"}'
              >
                Start a Free Trial
              </Button>
              {/* <Button size="lg" variant="rebolt-outline" className="bg-transparent" onClick={() => setVideoOpen(true)}>
                Watch 60-Second Demo
              </Button> */}
            </div>
          </div>
        </div>
      </section>

      <BaseVideoModal open={videoOpen} onClose={() => setVideoOpen(false)} url={DEMO_VIDEO_URL} />
    </>
  );
}

const TESTIMONIALS = [
  {
    quote: "We cut quoting time in half. Our techs love how easy it is to build quotes from the truck.",
    name: "Mike R.",
    company: "Reynolds Plumbing Co.",
    role: "Owner",
  },
  {
    quote: "Cleaner quotes and faster approvals. Clients actually read them now instead of calling with questions.",
    name: "Sarah C.",
    company: "Premier HVAC Services",
    role: "Operations Manager",
  },
  {
    quote: "The voice-to-quote feature is a game changer. I can quote a job while walking back to my van.",
    name: "Juan M.",
    company: "Martinez Electric",
    role: "Lead Electrician",
  },
  {
    quote: "Our close rate went up 23% after switching. The optional line items let clients build their own package.",
    name: "David T.",
    company: "Thompson Roofing",
    role: "Sales Manager",
  },
  {
    quote: "No more forgotten follow-ups. The automated reminders have saved us thousands in lost quotes.",
    name: "Lisa P.",
    company: "Comfort Zone HVAC",
    role: "Owner",
  },
  {
    quote: "Finally, professional-looking quotes that match our brand. Clients take us more seriously now.",
    name: "Robert W.",
    company: "Wilson Home Services",
    role: "General Manager",
  },
  {
    quote: "The price book saves us so much time. New techs can quote accurately on day one.",
    name: "Amanda F.",
    company: "Foster Mechanical",
    role: "Training Coordinator",
  },
  {
    quote: "Approvals come in faster because clients can sign right from their phone. No more chasing signatures.",
    name: "Chris A.",
    company: "Anderson Plumbing & Heating",
    role: "Owner",
  },
];

function TestimonialCard({ testimonial }: { testimonial: (typeof TESTIMONIALS)[0] }) {
  return (
    <div className="w-[350px] shrink-0 bg-white rounded-2xl p-6 shadow-lg border border-ash-100 mx-3">
      <div className="flex items-center gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-honey-500 text-honey-500" />
        ))}
      </div>
      <p className="text-ash-700 mb-4 leading-relaxed">&ldquo;{testimonial.quote}&rdquo;</p>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-linear-to-br from-rebolt-400 to-rebolt-600 flex items-center justify-center text-white font-semibold text-sm">
          {testimonial.name
            .split(" ")
            .map((n) => n[0])
            .join("")}
        </div>
        <div>
          <p className="font-medium text-ash-900 text-sm">{testimonial.name}</p>
          <p className="text-xs text-ash-500">
            {testimonial.role}, {testimonial.company}
          </p>
        </div>
      </div>
    </div>
  );
}

function TrustStrip() {
  const firstRow = TESTIMONIALS.slice(0, 4);
  const secondRow = TESTIMONIALS.slice(4);

  return (
    <section className="w-full py-36 bg-white overflow-hidden">
      <div className="max-w-(--breakpoint-xl) mx-auto px-4 mb-10">
        <p className="text-center text-rebolt-500 font-semibold uppercase tracking-wider text-lg">
          Trusted by modern home service teams
        </p>
      </div>

      <div className="relative">
        {/* Gradient overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-linear-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-linear-to-l from-white to-transparent z-10 pointer-events-none" />

        {/* First row - scrolls left */}
        <Marquee pauseOnHover className="[--duration:60s] mb-6">
          {firstRow.map((testimonial, idx) => (
            <TestimonialCard key={idx} testimonial={testimonial} />
          ))}
        </Marquee>

        {/* Second row - scrolls right */}
        <Marquee pauseOnHover reverse className="[--duration:60s]">
          {secondRow.map((testimonial, idx) => (
            <TestimonialCard key={idx} testimonial={testimonial} />
          ))}
        </Marquee>
      </div>
    </section>
  );
}

function FeatureBlockQuoteCreation() {
  return (
    <section className="w-full py-36 px-4 bg-clay-100 relative">
      <Image src="/HeroGrid.png" alt="" fill className="absolute inset-0 w-full object-cover -z-10 opacity-50" />
      <div className="max-w-(--breakpoint-xl) mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <p className="text-rebolt-600 font-semibold uppercase tracking-wider">Fast Quote Creation</p>
              <h2 className="font-clash text-4xl md:text-5xl font-medium text-ash-900 leading-tight">
                Create quotes and estimates in minutes
              </h2>
              <p className="text-xl text-ash-600">
                Build clean, customer-ready job quotes and service estimates without spreadsheets, back-and-forth, or
                messy notes. Rebolt makes it easy to quote on-site, then send instantly.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {[
                "Add services, labor, materials, and notes in seconds",
                "Build professional proposals clients understand",
                "Reuse your best quote layouts (consistent every time)",
                "Send by email/text and track status (Sent / Viewed / Approved)",
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="mt-1 w-6 h-6 rounded-full bg-rebolt-100 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-rebolt-600" />
                  </div>
                  <p className="text-lg text-ash-700">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            {/* Mock Quote Builder Interface */}
            <div className="bg-white rounded-3xl shadow-2xl border border-ash-200 overflow-hidden">
              {/* Progress Steps */}
              <div className="px-6 py-4 bg-ash-50 border-b border-ash-200">
                <div className="flex items-center justify-between max-w-xs mx-auto">
                  {[
                    { step: "1", label: "Add Items", active: true, complete: true },
                    { step: "2", label: "Preview", active: true, complete: false },
                    { step: "3", label: "Send", active: false, complete: false },
                  ].map((s, idx) => (
                    <div key={idx} className="flex items-center">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            s.complete
                              ? "bg-mint-500 text-white"
                              : s.active
                                ? "bg-rebolt-500 text-white"
                                : "bg-ash-200 text-ash-500"
                          }`}
                        >
                          {s.complete ? <Check className="w-4 h-4" /> : s.step}
                        </div>
                        <span className={`text-xs mt-1 ${s.active ? "text-ash-900 font-medium" : "text-ash-500"}`}>
                          {s.label}
                        </span>
                      </div>
                      {idx < 2 && (
                        <div className={`w-12 h-0.5 mx-2 mb-4 ${s.complete ? "bg-mint-500" : "bg-ash-200"}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Quote Content */}
              <div className="p-6">
                {/* Customer Info */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-ash-100">
                  <div>
                    <p className="text-xs text-ash-500 uppercase tracking-wider">Customer</p>
                    <p className="font-semibold text-ash-900">Sarah Johnson</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-ash-500 uppercase tracking-wider">Quote #</p>
                    <p className="font-semibold text-ash-900">1089</p>
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-3 mb-6">
                  {[
                    { item: "Electrical Panel Upgrade", type: "Service", price: "$2,400" },
                    { item: "Labor (4 hours @ $95/hr)", type: "Labor", price: "$380" },
                    { item: "200A Breaker Panel", type: "Material", price: "$485" },
                    { item: "Permit Fee", type: "Other", price: "$125" },
                  ].map((line, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-ash-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-rebolt-500" />
                        <div>
                          <p className="font-medium text-ash-900 text-sm">{line.item}</p>
                          <p className="text-xs text-ash-500">{line.type}</p>
                        </div>
                      </div>
                      <span className="font-semibold text-ash-900">{line.price}</span>
                    </div>
                  ))}
                </div>

                {/* Notes */}
                <div className="mb-6">
                  <p className="text-xs text-ash-500 uppercase tracking-wider mb-2">Notes</p>
                  <div className="p-3 bg-honey-50 rounded-xl border border-honey-100">
                    <p className="text-sm text-ash-700">
                      Includes cleanup and haul-away of old panel. Warranty: 1 year labor, 10 year parts.
                    </p>
                  </div>
                </div>

                {/* Total & Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-ash-200">
                  <div>
                    <p className="text-xs text-ash-500">Total</p>
                    <p className="text-2xl font-bold text-ash-900">$3,390</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 border border-ash-300 rounded-full text-sm font-medium text-ash-700 hover:bg-ash-50 transition-colors">
                      Preview
                    </button>
                    <button className="px-4 py-2 bg-rebolt-500 rounded-full text-sm font-medium text-white hover:bg-rebolt-600 transition-colors flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Quote
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureBlockVoiceToQuote({ calPath }: { calPath: string }) {
  return (
    <section
      id="voice-to-quote"
      className="w-full py-36 px-4 bg-ash-900 text-white relative overflow-hidden scroll-mt-32"
    >
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
          backgroundSize: "30px 30px",
        }}
      />
      <div className="max-w-(--breakpoint-xl) mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <div className="bg-ash-800 rounded-3xl p-8 border border-ash-700">
              {/* Microphone visual */}
              <div className="flex items-center justify-center mb-8">
                <div className="w-24 h-24 rounded-full bg-rebolt-500 flex items-center justify-center animate-pulse">
                  <Mic className="w-12 h-12 text-white" />
                </div>
              </div>

              {/* Voice input example */}
              <div className="bg-ash-700 rounded-2xl p-6 mb-6">
                <p className="text-ash-300 text-sm uppercase tracking-wider mb-2">You say:</p>
                <p className="text-white text-lg italic">
                  &ldquo;Replace kitchen faucet, install customer-supplied disposal, add new shutoff valves&rdquo;
                </p>
              </div>

              {/* Arrow */}
              <div className="flex justify-center mb-6">
                <div className="w-10 h-10 rounded-full bg-rebolt-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              </div>

              {/* Output */}
              <div className="bg-white rounded-2xl p-6 text-ash-900">
                <p className="text-rebolt-600 text-sm uppercase tracking-wider mb-3">Rebolt creates:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-ash-100 pb-2">
                    <span>Kitchen Faucet Replacement</span>
                    <span className="font-medium">$285.00</span>
                  </div>
                  <div className="flex justify-between border-b border-ash-100 pb-2">
                    <span>Disposal Installation (customer-supplied)</span>
                    <span className="font-medium">$175.00</span>
                  </div>
                  <div className="flex justify-between border-b border-ash-100 pb-2">
                    <span>Shutoff Valve Installation (x2)</span>
                    <span className="font-medium">$120.00</span>
                  </div>
                  <div className="flex justify-between pt-2 font-semibold">
                    <span>Total</span>
                    <span>$580.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-8 order-1 lg:order-2">
            <div className="flex flex-col gap-4">
              <p className="text-rebolt-400 font-semibold uppercase tracking-wider">Signature Feature</p>
              <h2 className="font-clash text-4xl md:text-5xl font-medium leading-tight">
                Voice to Quote: speak your estimate into existence
              </h2>
              <p className="text-xl text-ash-300">
                When you&apos;re in the field, typing slows you down. With Voice to Quote, you can speak naturally and
                generate a structured quote—line items, scope, and a customer-friendly proposal—without stopping the
                job.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {[
                "Perfect for techs who want to quote fast from the truck",
                "Keeps details accurate while everything is fresh",
                "Works on mobile so you can quote wherever you are",
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="mt-1 w-6 h-6 rounded-full bg-rebolt-500 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-lg text-ash-200">{item}</p>
                </div>
              ))}
            </div>

            <div className="pt-4">
              <Button
                size="lg"
                variant="rebolt-primary"
                data-cal-link={calPath}
                data-cal-config='{"layout":"month_view"}'
              >
                Try Voice to Quote
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureBlockPriceBook() {
  return (
    <section id="price-book" className="w-full py-36 px-4 bg-white scroll-mt-32">
      <div className="max-w-(--breakpoint-xl) mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            {/* Mock Price Book UI */}
            <div className="bg-white rounded-3xl shadow-2xl border border-ash-200 overflow-hidden">
              {/* Header */}
              <div className="bg-ash-50 px-6 py-4 border-b border-ash-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-ash-500 uppercase tracking-wider">Price Book</p>
                    <p className="font-semibold text-ash-900">Plumbing Services</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="px-3 py-1 bg-white rounded-full border border-ash-200 text-xs text-ash-600">
                      All Categories
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Book Items */}
              <div className="divide-y divide-ash-100">
                {[
                  { name: "Faucet Repair", category: "Fixtures", price: "$125", added: false },
                  { name: "Toilet Installation", category: "Fixtures", price: "$285", added: true },
                  { name: "Water Heater Flush", category: "Maintenance", price: "$175", added: false },
                  { name: "Drain Cleaning", category: "Drains", price: "$195", added: true },
                  { name: "Garbage Disposal Install", category: "Fixtures", price: "$225", added: false },
                  { name: "Pipe Leak Repair", category: "Repairs", price: "$165", added: false },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className={`px-6 py-4 flex items-center justify-between hover:bg-ash-50 transition-colors ${item.added ? "bg-mint-50" : ""}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.added ? "bg-mint-100" : "bg-ash-100"}`}
                      >
                        {item.added ? (
                          <CircleCheckBig className="w-4 h-4 text-mint-600" />
                        ) : (
                          <FileText className="w-4 h-4 text-ash-400" />
                        )}
                      </div>
                      <div>
                        <p className={`font-medium ${item.added ? "text-mint-700" : "text-ash-900"}`}>{item.name}</p>
                        <p className="text-xs text-ash-500">{item.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-semibold text-ash-900">{item.price}</span>
                      <button
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          item.added
                            ? "bg-mint-100 text-mint-700 border border-mint-200"
                            : "bg-rebolt-500 text-white hover:bg-rebolt-600"
                        }`}
                      >
                        {item.added ? "Added" : "Add"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer - Quote Summary */}
              <div className="bg-ash-900 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-ash-400 text-xs">Current Quote</p>
                    <p className="text-white font-medium">2 items added</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-bold text-lg">$480</span>
                    <button className="px-4 py-2 bg-rebolt-500 text-white rounded-full text-sm font-medium hover:bg-rebolt-600 transition-colors">
                      View Quote
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-8 order-1 lg:order-2">
            <div className="flex flex-col gap-4">
              <p className="text-rebolt-600 font-semibold uppercase tracking-wider">Price Book & Templates</p>
              <h2 className="font-clash text-4xl md:text-5xl font-medium text-ash-900 leading-tight">
                Price book estimating for your go-to jobs
              </h2>
              <p className="text-xl text-ash-600">
                Stop reinventing the wheel. Use a price book to store your common services and
                &ldquo;bread-and-butter&rdquo; jobs so you can build consistent estimates faster—and protect your
                margins.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {[
                "Save your most common jobs as ready-to-use items",
                "Standardize pricing across your team",
                "Quickly adjust quantities, labor, or materials on-site",
                "Reduce missed line items and underbidding",
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="mt-1 w-6 h-6 rounded-full bg-rebolt-100 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-rebolt-600" />
                  </div>
                  <p className="text-lg text-ash-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureBlockOptionalLineItems() {
  return (
    <section id="optional-line-items" className="w-full py-36 px-4 bg-clay-100 scroll-mt-32">
      <Image src="/HeroGrid.png" alt="" fill className="absolute inset-0 w-full object-cover -z-10 opacity-50" />
      <div className="max-w-(--breakpoint-xl) mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <p className="text-rebolt-600 font-semibold uppercase tracking-wider">Upsell Without Friction</p>
              <h2 className="font-clash text-4xl md:text-5xl font-medium text-ash-900 leading-tight">
                Optional line items that increase average ticket
              </h2>
              <p className="text-xl text-ash-600">
                Give clients choices without rewriting quotes. Add optional line items (upgrades, alternates, add-ons)
                so clients can build the right package—and you can raise close rate and ticket size.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {[
                "Keep the base scope clean while enabling add-ons",
                "Customer can select options during approval",
                "Add upgrades (replace instead of repair, premium materials)",
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="mt-1 w-6 h-6 rounded-full bg-rebolt-100 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-rebolt-600" />
                  </div>
                  <p className="text-lg text-ash-700">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            {/* Mock Quote with Optional Line Items */}
            <div className="bg-white rounded-3xl shadow-2xl border border-ash-200 overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-ash-200">
                <p className="text-xs text-ash-500 uppercase tracking-wider mb-1">Quote #1087</p>
                <p className="font-semibold text-ash-900">HVAC System Replacement</p>
              </div>

              {/* Base Scope */}
              <div className="p-6 border-b border-ash-200">
                <p className="text-xs text-ash-500 uppercase tracking-wider mb-4">Base Scope (Included)</p>
                <div className="space-y-3">
                  {[
                    { item: "3-Ton AC Unit Installation", price: "$4,200" },
                    { item: "Ductwork Inspection", price: "$150" },
                    { item: "Thermostat Setup", price: "$85" },
                  ].map((line, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CircleCheckBig className="w-5 h-5 text-mint-500" />
                        <span className="text-ash-700">{line.item}</span>
                      </div>
                      <span className="font-medium text-ash-900">{line.price}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-ash-100 flex justify-between">
                  <span className="font-medium text-ash-600">Base Total</span>
                  <span className="font-bold text-ash-900">$4,435</span>
                </div>
              </div>

              {/* Optional Upgrades */}
              <div className="p-6 bg-rebolt-50">
                <p className="text-xs text-rebolt-600 uppercase tracking-wider mb-4">Optional Upgrades</p>
                <div className="space-y-4">
                  {[
                    { item: "Smart Thermostat (Ecobee)", price: "+$289", selected: true },
                    { item: "10-Year Extended Warranty", price: "+$450", selected: true },
                    { item: "UV Air Purifier", price: "+$375", selected: false },
                    { item: "Duct Sealing & Cleaning", price: "+$325", selected: false },
                  ].map((upgrade, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                        upgrade.selected ? "bg-white border-2 border-rebolt-300" : "bg-white/50 border border-ash-200"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded-md flex items-center justify-center ${
                            upgrade.selected ? "bg-rebolt-500" : "bg-ash-200"
                          }`}
                        >
                          {upgrade.selected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className={upgrade.selected ? "text-ash-900 font-medium" : "text-ash-600"}>
                          {upgrade.item}
                        </span>
                      </div>
                      <span className={`font-medium ${upgrade.selected ? "text-rebolt-600" : "text-ash-500"}`}>
                        {upgrade.price}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="px-6 py-4 bg-ash-900 flex items-center justify-between">
                <div>
                  <p className="text-ash-400 text-xs">With Selected Upgrades</p>
                  <p className="text-white font-medium">2 add-ons selected</p>
                </div>
                <div className="text-right">
                  <p className="text-ash-400 text-xs">New Total</p>
                  <p className="text-white font-bold text-2xl">$5,174</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureBlockOnlineApprovals() {
  return (
    <section id="online-approvals" className="w-full py-36 px-4 bg-white scroll-mt-32">
      <div className="max-w-(--breakpoint-xl) mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            {/* Mock Client Portal Approval Screen */}
            <div className="bg-white rounded-3xl shadow-2xl border border-ash-200 overflow-hidden max-w-sm mx-auto">
              {/* Header */}
              <div className="bg-ash-900 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                    <Zap className="w-5 h-5 text-ash-900" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">ABC Plumbing Co.</p>
                    <p className="text-ash-400 text-xs">Quote #1042</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Quote Items */}
                <div className="mb-6">
                  <p className="text-xs text-ash-500 uppercase tracking-wider mb-3">Scope of Work</p>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-ash-700">Water Heater Replacement</span>
                      <span className="font-medium text-ash-900">$1,850</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-ash-700">Permit & Inspection</span>
                      <span className="font-medium text-ash-900">$175</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-ash-700">Old Unit Disposal</span>
                      <span className="font-medium text-ash-900">$75</span>
                    </div>
                  </div>
                </div>

                {/* Optional Add-ons */}
                <div className="mb-6 p-4 bg-rebolt-50 rounded-xl border border-rebolt-100">
                  <p className="text-xs text-rebolt-600 uppercase tracking-wider mb-3">Optional Add-ons</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ash-700">Expansion Tank</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-ash-900">+$185</span>
                        <div className="w-10 h-6 bg-rebolt-500 rounded-full relative">
                          <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ash-700">10-Year Warranty</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-ash-500">+$249</span>
                        <div className="w-10 h-6 bg-ash-300 rounded-full relative">
                          <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="flex justify-between items-center py-4 border-t border-ash-200 mb-6">
                  <span className="text-lg font-medium text-ash-900">Total</span>
                  <span className="text-2xl font-bold text-ash-900">$2,285</span>
                </div>

                {/* Signature Area */}
                <div className="mb-4">
                  <p className="text-xs text-ash-500 mb-2">Your Signature</p>
                  <div className="h-16 border-2 border-dashed border-ash-300 rounded-lg flex items-center justify-center">
                    <p className="text-ash-400 italic text-lg font-script">John Smith</p>
                  </div>
                </div>

                {/* Approve Button */}
                <button className="w-full bg-mint-500 hover:bg-mint-600 text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors">
                  <CircleCheckBig className="w-5 h-5" />
                  Approve Quote
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-8 order-1 lg:order-2">
            <div className="flex flex-col gap-4">
              <p className="text-rebolt-600 font-semibold uppercase tracking-wider">Client Portal</p>
              <h2 className="font-clash text-4xl md:text-5xl font-medium text-ash-900 leading-tight">
                Online quote approvals clients can sign off in seconds
              </h2>
              <p className="text-xl text-ash-600">
                Make it effortless for clients to approve. Send a quote through the client portal, so they can review
                details, choose options, and approve without calls or paperwork.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {[
                "Mobile-friendly approval flow",
                "Clear scope + pricing presentation (reduces confusion)",
                "Instant approval notifications to your team",
                "Faster approvals = faster scheduling and revenue",
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="mt-1 w-6 h-6 rounded-full bg-rebolt-100 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-rebolt-600" />
                  </div>
                  <p className="text-lg text-ash-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureBlockAutomatedFollowUps() {
  return (
    <section id="automated-follow-ups" className="w-full py-36 px-4 bg-clay-100 scroll-mt-32">
      <div className="max-w-(--breakpoint-xl) mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <p className="text-rebolt-600 font-semibold uppercase tracking-wider">Never Lose a Quote</p>
              <h2 className="font-clash text-4xl md:text-5xl font-medium text-ash-900 leading-tight">
                Automated quote follow-ups that win back busy clients
              </h2>
              <p className="text-xl text-ash-600">
                Most quotes aren&apos;t rejected—they&apos;re forgotten. Rebolt automatically follows up so you
                don&apos;t leave money on the table, without your team chasing every estimate manually.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {[
                "Automatically nudge clients who haven't responded",
                "Keep your pipeline moving while you're on jobs",
                "Reduce no-response quotes and lost opportunities",
                "Stay consistent without sounding pushy (configurable tone/timing)",
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="mt-1 w-6 h-6 rounded-full bg-rebolt-100 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-rebolt-600" />
                  </div>
                  <p className="text-lg text-ash-700">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            {/* Timeline visual */}
            <div className="bg-white rounded-3xl shadow-xl border border-ash-200 p-8">
              <p className="text-sm text-ash-500 uppercase tracking-wider mb-6">Automated Follow-Up Timeline</p>
              <div className="relative">
                {/* Connector line */}
                <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-ash-200" />

                <div className="space-y-8">
                  {[
                    { label: "Sent", icon: Send, color: "bg-rebolt-500", time: "Day 1" },
                    { label: "Viewed", icon: FileText, color: "bg-honey-500", time: "Day 1" },
                    {
                      label: "Follow-up #1",
                      icon: MessageSquare,
                      color: "bg-ash-400",
                      time: "Day 3",
                    },
                    {
                      label: "Follow-up #2",
                      icon: MessageSquare,
                      color: "bg-ash-400",
                      time: "Day 7",
                    },
                    { label: "Approved!", icon: CircleCheckBig, color: "bg-mint-500", time: "Day 8" },
                  ].map((step, idx) => (
                    <div key={idx} className="flex items-center gap-4 relative z-10">
                      <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", step.color)}>
                        <step.icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-ash-900">{step.label}</p>
                        <p className="text-sm text-ash-500">{step.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlatformSection({ calPath }: { calPath: string }) {
  return (
    <section id="mobile-app" className="w-full py-36 px-4 bg-white scroll-mt-32">
      <div className="max-w-(--breakpoint-xl) mx-auto text-center">
        <p className="text-rebolt-500 font-semibold uppercase tracking-wider mb-4">Built for the Field</p>
        <h2 className="font-clash text-4xl md:text-5xl lg:text-6xl font-medium text-ash-900 mb-6">
          Quoting software built for the field{" "}
          <span className="relative inline-block">
            (and the office)
            <UnderlineSVG className="absolute -bottom-2 md:-bottom-3 left-1/2 -translate-x-1/2 w-full text-rebolt-500" />
          </span>
        </h2>
        <p className="text-xl text-ash-600 max-w-3xl mx-auto mb-16">
          Rebolt works wherever you do: web app + iOS + Android. It&apos;s designed to be simple enough for your whole
          team—techs, office staff, and owners—without weeks of training.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {[
            { icon: Monitor, label: "Web App" },
            { icon: Smartphone, label: "iOS App" },
            { icon: Phone, label: "Android App" },
            { icon: Zap, label: "Fast UI" },
          ].map((item, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center justify-center gap-4 p-8 rounded-2xl bg-ash-50 border border-ash-200 hover:border-rebolt-200 "
            >
              <item.icon className="w-10 h-10 text-ash-400 " />
              <span className="font-medium text-ash-800 text-lg">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-4 items-center">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              size="lg"
              variant="rebolt-primary"
              className="text-lg px-8"
              data-cal-link={calPath}
              data-cal-config='{"layout":"month_view"}'
            >
              Start Free Trial
            </Button>
            {/* <Button
              size="lg"
              variant="rebolt-outline"
              className="text-lg px-8"
              onClick={() => {
                document.getElementById("voice-to-quote")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Watch Demo
            </Button> */}
          </div>
          <p className="text-sm text-ash-500">All-in-one simple pricing &bull; Free trial available</p>
        </div>
      </div>
    </section>
  );
}

function WorkflowSection() {
  const steps = [
    {
      number: "1",
      title: "Build your quote",
      description: "Use the price book or Voice to Quote to create an estimate in minutes",
    },
    {
      number: "2",
      title: "Add options",
      description: "Include optional line items for upgrades and add-ons",
    },
    {
      number: "3",
      title: "Send instantly",
      description: "Email or text the quote from mobile or desktop",
    },
    {
      number: "4",
      title: "Get approved",
      description: "Customer approves online + automatic follow-ups until it's closed",
    },
  ];

  return (
    <section className="w-full py-36 px-4 bg-ash-900 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-linear-to-l from-ash-800 to-transparent pointer-events-none" />
      <div className="max-w-(--breakpoint-xl) mx-auto relative z-10">
        <div className="text-center mb-16">
          <p className="text-rebolt-400 font-semibold uppercase tracking-wider mb-4">Simple Workflow</p>
          <h2 className="font-clash text-4xl md:text-5xl lg:text-6xl font-medium mb-6">
            From request to approved quote—
            <br className="hidden md:block" />
            without the chaos
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative mt-24">
          {/* Connector Line (Desktop) */}
          <div className="hidden md:block absolute top-12 left-[12%] right-[12%] h-0.5 bg-ash-700 border-t border-dashed border-ash-600 z-0" />

          {steps.map((step, idx) => (
            <div key={idx} className="flex flex-col items-center text-center gap-6 relative z-10">
              <div
                className={cn(
                  "w-24 h-24 rounded-full flex items-center justify-center shadow-xl",
                  idx === 0 ? "bg-rebolt-600 border-4 border-rebolt-500" : "bg-ash-800 border-4 border-ash-700"
                )}
              >
                <span className="text-3xl font-clash font-bold text-white">{step.number}</span>
              </div>
              <h3 className="text-xl md:text-2xl font-medium text-white">{step.title}</h3>
              <p className="text-ash-300">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section id="faq" className="w-full py-36 px-4 bg-white scroll-mt-32">
      <div className="max-w-(--breakpoint-xl) mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-clash text-4xl md:text-5xl font-medium text-ash-900">
            Quoting & estimating software{" "}
            <span className="relative inline-block">
              FAQ
              <UnderlineSVG className="absolute -bottom-2 md:-bottom-3 left-1/2 -translate-x-1/2 w-full text-rebolt-500" />
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FAQ_ITEMS.map((item, index) => (
            <div key={index} className="flex flex-col gap-3 bg-ash-50 rounded-3xl p-6 md:p-8">
              <h3 className="font-clash font-medium text-xl md:text-2xl leading-tight tracking-snug text-ash-900">
                {item.question}
              </h3>
              <p className="text-base md:text-lg text-ash-600 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
